import type { GameState, Move, TransitionEvent } from "../game";

import { BOARD_SIZE, type VisualInventory } from "./extractionPlanner";
import { planExtractionSequence, type ExtractionSequence } from "./extractionSequence";

/** Builds presentation-only extraction data from an already-resolved engine transition. */
export function planTransitionExtraction(
  previousGame: GameState,
  move: Move,
  events: readonly TransitionEvent[],
): ExtractionSequence | null {
  const sourceRemoval = events.find((event) => event.kind === "field-removed");
  if (sourceRemoval?.kind !== "field-removed") return null;

  const orphanEvent = events.find((event) => event.kind === "orphan-cleanup");
  const departureVector = {
    row: Math.sign(move.to.row - move.from.row),
    column: Math.sign(move.to.column - move.from.column),
  };

  return planExtractionSequence({
    inventory: visualInventory(previousGame),
    source: { position: sourceRemoval.position, departureVector },
    orphans:
      orphanEvent?.kind === "orphan-cleanup"
        ? orphanEvent.fields.map(({ position }) => ({ position }))
        : [],
  });
}

export function visualInventory(state: GameState): VisualInventory {
  return Object.freeze(
    Array.from({ length: BOARD_SIZE }, (_, row) =>
      Object.freeze(
        Array.from({ length: BOARD_SIZE }, (_, column) => {
          const cell = state.cells[row * BOARD_SIZE + column];
          if (cell === undefined) {
            throw new RangeError(`Game state is missing cell (${row}, ${column}).`);
          }
          return Object.freeze(
            cell.kind === "removed"
              ? { kind: "gap" as const }
              : { kind: "slab" as const, discCount: cell.stack.length },
          );
        }),
      ),
    ),
  );
}
